import React from "react";

import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import { Button as SettingButton } from "@app/analytics/account/components/styles";
import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import type {
  IntegrationProviderId,
  UserDistributionSetting,
} from "@app/common/types";

import Checkbox from "@rui/Checkbox";
import { Column, Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import type { DistributionChannelConfig } from "./constants";
import DistributionRecipientsSelect from "./DistributionRecipientsSelect";
import FrequencySelect from "./FrequencySelect";
import { ChannelRow } from "./styles";
import type { DistributionChannelDisabledReason } from "./utils";

interface DistributionChannelRowProps {
  config: DistributionChannelConfig;
  children?: React.ReactNode;
  defaultFrequency?: UserDistributionSetting["frequency"];
  defaultSendTo?: UserDistributionSetting["sendTo"];
  disabledReason?: DistributionChannelDisabledReason;
  name: string;
  onSettingChange?: (updatedConfig: UserDistributionSetting) => void;
  onToggleEnabled: (
    channel: UserDistributionSetting["method"],
    enable: boolean,
    existingSetting?: UserDistributionSetting
  ) => void;
  setting?: UserDistributionSetting;
}

const TOOLTIP_BY_DISABLED_REASON: {
  [key in DistributionChannelDisabledReason]: string;
} = {
  disabled_for_workspace:
    "Your Workspace has restricted access to this integration. It is not available to use with Read at this time.",
  not_connected: "Connect to enable",
};

const DistributionChannelRow = ({
  config,
  children,
  defaultFrequency,
  defaultSendTo,
  disabledReason,
  name,
  onSettingChange,
  onToggleEnabled,
  setting,
}: DistributionChannelRowProps) => {
  const isDisabled = disabledReason != null;
  const channelIsEnabled = !isDisabled && Boolean(setting?.isEnabled);
  const { getEnableUrl } = useIntegrations();
  const { authorize, enable } = useIntegration();

  const handleChange = (_e: React.SyntheticEvent, checked: boolean) => {
    onToggleEnabled(config.channel, checked, setting);
  };

  const handleAuthorizeProvider = (providerId: IntegrationProviderId) => {
    authorize.mutate({
      providerId,
      returnRoute: location.pathname,
      scope: "user",
    });
  };

  const handleClickConnect = () => {
    if (!config.providerId) return;

    const enableUrl = getEnableUrl({ providerId: config.providerId });
    // if we can enable w/o pushing through an auth flow, try that first
    if (enableUrl) {
      enable.mutate(enableUrl, {
        onError: () => handleAuthorizeProvider(config.providerId!),
      });
    } else {
      handleAuthorizeProvider(config.providerId);
    }
  };

  return (
    <ChannelRow variant="apart">
      <Column>
        <TooltipWrapper
          show={isDisabled}
          title={
            TOOLTIP_BY_DISABLED_REASON[
              disabledReason as DistributionChannelDisabledReason
            ]
          }
        >
          <FormControlLabel
            checked={channelIsEnabled}
            control={<Checkbox variant="filled" />}
            label={
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                {config.label}
              </Typography>
            }
            disabled={isDisabled}
            onChange={handleChange}
          />
        </TooltipWrapper>
        {children}
      </Column>
      {channelIsEnabled ? (
        <Row variant="right" spacing={2}>
          <DistributionRecipientsSelect
            name={`${name}-${config.channel}-send-to`}
            channel={config.channel}
            defaultValue={defaultSendTo}
            options={config.recipients}
            onChange={onSettingChange}
            setting={setting}
          />
          <FrequencySelect
            name={`${name}-${config.channel}-frequency`}
            defaultValue={defaultFrequency}
            options={config.frequencies}
            onChange={onSettingChange}
            setting={setting}
          />
        </Row>
      ) : disabledReason === "not_connected" ? (
        <SettingButton
          onClick={handleClickConnect}
          sx={{ height: "fit-content", padding: 1 }}
        >
          Connect
        </SettingButton>
      ) : null}
    </ChannelRow>
  );
};

export default DistributionChannelRow;
